import axios from "axios";
import { ApiError } from "../errors/index.js";
export class ApiClient {
    constructor(config, refreshTokenEndpoint = "/api/v1/auth/refresh") {
        this.csrfToken = null;
        this.instance = axios.create({
            baseURL: config.baseURL,
            timeout: config.timeout || 60000,
            headers: {
                ...config.headers,
            },
            withCredentials: true, // Always ensure cookies are sent
        });
        this.refreshTokenEndpoint = refreshTokenEndpoint;
        // Add request interceptor for CSRF token
        this.instance.interceptors.request.use(config => {
            // Add CSRF token for state-changing requests
            if (["post", "put", "delete", "patch"].includes(config.method?.toLowerCase() || "")) {
                if (this.csrfToken) {
                    config.headers["X-CSRF-Token"] = this.csrfToken;
                }
                else {
                    // Try to get the token from cookies - helps with timing issues
                    const tokenFromCookies = this.getCsrfTokenFromCookies();
                    if (tokenFromCookies) {
                        this.setCsrfToken(tokenFromCookies);
                        config.headers["X-CSRF-Token"] = tokenFromCookies;
                    }
                }
            }
            return config;
        }, error => {
            const errorDetails = {
                message: error.message,
                name: error.name,
                code: error.code,
                request: {
                    method: error.config?.method?.toUpperCase(),
                    url: error.config?.url,
                    baseURL: error.config?.baseURL,
                    timeout: error.config?.timeout,
                    params: error.config?.params,
                    data: error.config?.data,
                    headers: error.config?.headers,
                },
                // Timing information
                timestamp: new Date().toISOString(),
                isTimeout: error.code === "ECONNABORTED" || error.message?.includes("timeout"),
                isNetworkError: error.code === "ERR_NETWORK" || error.message?.includes("Network Error"),
                isCanceled: axios.isCancel(error),
                isAxiosError: error.isAxiosError,
            };
            // Only log to console - no Sentry here (these are rare request setup errors)
            console.error("🔴 Request Interceptor Error:", JSON.stringify(errorDetails, null, 2));
            return Promise.reject(error);
        });
        // Add response interceptor for handling authentication errors
        this.instance.interceptors.response.use(response => {
            // Look for CSRF token in cookies
            const csrfToken = this.getCsrfTokenFromCookies();
            if (csrfToken) {
                this.setCsrfToken(csrfToken);
            }
            return response;
        }, async (error) => {
            const originalRequest = error.config;
            // If error is 401 or 403 CSRF and we haven't already tried to refresh
            if ((error.response?.status === 401 ||
                (error.response?.status === 403 && error.response?.data?.message?.includes("CSRF"))) &&
                !originalRequest._retry) {
                originalRequest._retry = true;
                try {
                    // Just call the refresh endpoint - cookies handled automatically
                    await axios.post(`${config.baseURL}${this.refreshTokenEndpoint}`, {}, { withCredentials: true });
                    // Check if we have a new CSRF token after refresh
                    const newToken = this.getCsrfTokenFromCookies();
                    if (newToken) {
                        this.setCsrfToken(newToken);
                        if (originalRequest.headers) {
                            originalRequest.headers["X-CSRF-Token"] = newToken;
                        }
                    }
                    // Retry the original request
                    return this.instance(originalRequest);
                }
                catch (refreshError) {
                    return Promise.reject(refreshError);
                }
            }
            // Comprehensive error logging - extract all available information
            const errorDetails = {
                // Basic error info
                message: error.message,
                name: error.name,
                code: error.code,
                stack: error.stack,
                // Request details
                request: {
                    method: error.config?.method?.toUpperCase(),
                    url: error.config?.url,
                    fullUrl: error.config?.baseURL
                        ? `${error.config.baseURL}${error.config.url}`
                        : error.config?.url,
                    baseURL: error.config?.baseURL,
                    timeout: error.config?.timeout,
                    params: error.config?.params,
                    data: error.config?.data,
                    headers: error.config?.headers,
                    withCredentials: error.config?.withCredentials,
                },
                // Response details (if available)
                response: error.response
                    ? {
                        status: error.response.status,
                        statusText: error.response.statusText,
                        data: error.response.data,
                        headers: error.response.headers,
                    }
                    : null,
                // Browser/Environment info
                environment: {
                    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "server",
                    platform: typeof navigator !== "undefined" ? navigator.platform : "server",
                    language: typeof navigator !== "undefined" ? navigator.language : "unknown",
                    online: typeof navigator !== "undefined" ? navigator.onLine : true,
                    cookieEnabled: typeof navigator !== "undefined" ? navigator.cookieEnabled : true,
                },
                // Timing information
                timestamp: new Date().toISOString(),
                // Error classification
                isTimeout: error.code === "ECONNABORTED" || error.message?.includes("timeout"),
                isNetworkError: error.code === "ERR_NETWORK" || error.message?.includes("Network Error"),
                isCanceled: axios.isCancel(error),
                isAxiosError: error.isAxiosError,
                // Additional axios error info
                axiosErrorDetails: error.toJSON ? error.toJSON() : null,
            };
            // Always log to console for debugging
            console.error("🔴 API Error", JSON.stringify(errorDetails, null, 2));
            // Convert axios error to ApiError
            return Promise.reject(new ApiError(error.response?.data?.message || error.message, error.response?.status || 500, error.response?.data || null));
        });
    }
    /**
     * Set the CSRF token for state-changing requests
     */
    setCsrfToken(token) {
        this.csrfToken = token;
    }
    /**
     * Get CSRF token from cookies
     */
    getCsrfTokenFromCookies() {
        // Browser environment check
        if (typeof document === "undefined")
            return null;
        // Parse cookies to find CSRF token
        const cookies = document.cookie.split(";");
        for (const cookie of cookies) {
            const [name, value] = cookie.trim().split("=");
            if (name === "scopex_csrf_token") {
                return value;
            }
        }
        return null;
    }
    /**
     * Make a GET request with validation
     */
    async get(url, schema, config) {
        const response = await this.instance.get(url, config);
        return this.validateResponse(response, schema);
    }
    /**
     * Make a POST request with validation
     */
    async post(url, data, schemaOrConfig, config) {
        // Check if third parameter is a config object instead of a schema
        let actualSchema;
        let actualConfig;
        if (schemaOrConfig &&
            typeof schemaOrConfig === "object" &&
            !("parse" in schemaOrConfig) &&
            ("headers" in schemaOrConfig || "responseType" in schemaOrConfig)) {
            // Third param is a config object
            actualConfig = schemaOrConfig;
            actualSchema = undefined;
        }
        else {
            // Normal case: third param is schema, fourth is config
            actualSchema = schemaOrConfig;
            actualConfig = config;
        }
        const response = await this.instance.post(url, data, actualConfig);
        // Skip validation for binary responses (when responseType is 'blob' or 'arraybuffer')
        if (actualConfig?.responseType === "blob" || actualConfig?.responseType === "arraybuffer") {
            return response.data;
        }
        return this.validateResponse(response, actualSchema);
    }
    /**
     * Make a PUT request with validation
     */
    async put(url, data, schema, config) {
        const response = await this.instance.put(url, data, config);
        return this.validateResponse(response, schema);
    }
    /**
     * Make a PATCH request with validation
     */
    async patch(url, data, schema, config) {
        const response = await this.instance.patch(url, data, config);
        return this.validateResponse(response, schema);
    }
    /**
     * Make a DELETE request with validation
     */
    async delete(url, schema, config) {
        const response = await this.instance.delete(url, config);
        return this.validateResponse(response, schema);
    }
    /**
     * Validate API response data against a schema
     */
    validateResponse(response, schema) {
        // Check if the response is a binary type (Blob or ArrayBuffer)
        if (response.data instanceof Blob ||
            response.data instanceof ArrayBuffer ||
            (response.config &&
                (response.config.responseType === "blob" || response.config.responseType === "arraybuffer"))) {
            // For binary responses, return the data directly without validation
            return response.data;
        }
        // The API's envelope ({ success, message, data }) comes back as it is; any other body becomes
        // its `data`. An answer that carries only a message has no `data` (a service that returns just
        // `_message`, e.g. the registration), and is an envelope too: wrapped again, its message would
        // read as "".
        const body = response.data;
        const isEnvelope = body &&
            typeof body === "object" &&
            ("data" in body || (typeof body.success === "boolean" && typeof body.message === "string"));
        const responseData = isEnvelope ? body : { success: true, data: body, message: "" };
        if (schema) {
            try {
                // If we have a schema, validate against it
                const parsedData = schema.parse(responseData.data);
                return parsedData;
            }
            catch (error) {
                throw new ApiError("Response validation failed", 400, {
                    validationError: error,
                    data: responseData.data,
                });
            }
        }
        // If no schema, return the entire response object (including success, data, message)
        return responseData;
    }
}
